import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Table as MantineTable,
  Menu,
  TableTrProps,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowsSort,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconDotsVertical,
} from '@tabler/icons-react';
import { DataTable, DataTableColumnTextAlign } from 'mantine-datatable';
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router';
import useSortableData from 'src/common/components/table/hooks/useSortableData';
import { SortableColumn } from 'src/common/components/table/types';
import { formatDateTime } from 'src/meetings/utils/date-utils';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { FlatRow } from '../fileTree';
import { useFileDragDrop } from '../context/FileDragDropContext';
import { createDragElement } from '../utils/createDragElement';
import FileNameWithIcon from './FileNameWithIcon';
import classes from './FilesTable.module.css';

type TableData = UploadedFile & {
  isGenerated?: boolean;
};

type PrimaryActionType<T> = {
  label: string;
  variant?: 'primary' | 'secondary';
  icon?: React.ComponentType<{ size?: number | string }>;
  condition?: (entityWithFile: T) => boolean;
} & (
  | { type: 'link'; getLink: (entityWithFile: T) => string; onClick?: (entityWithFile: T) => void }
  | { type: 'action'; onClick: (entityWithFile: T) => void }
);

type TreeProps = {
  /** Flat ordered rows produced by flattenVisible; enables tree rendering with depth indentation and expand/collapse. */
  treeRows: FlatRow[];
  /** Called when a folder row is clicked; consumer toggles the id in its expandedIds set. */
  onToggleExpand: (id: string) => void;
};

type NoTreeProps = {
  treeRows?: undefined;
  onToggleExpand?: undefined;
};

type Selection<T> = {
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  isSelectable?: (record: T) => boolean;
};

type Props<T extends TableData> = {
  entitiesWithFile: T[];
  isLoading: boolean;
  onClick?: (entityWithFile: T) => void;
  additionalColumns?: FilesTableColumn<T>[];
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
  enableDragDrop?: boolean;
  inlineActions?: (record: T) => ReactNode;
  nameSuffix?: (record: T) => ReactNode;
  nameSubtitle?: (record: T) => string | undefined;
  actionsColumnTitle?: string;
  nameColumnTitle?: string;
  variant?: 'default' | 'compact';
  /** When true, disables drag-drop and suppresses all row actions. */
  readOnly?: boolean;
  /** Per-row predicate; when true the row cannot be a drag source or drop target. */
  isRowReadOnly?: (record: T) => boolean;
  selection?: Selection<T>;
} & (TreeProps | NoTreeProps);

export type FilesTableColumn<T> = {
  label: string;
  valueBuilder: (record: T) => ReactElement;
  textAlign?: DataTableColumnTextAlign;
  sortable?: boolean;
  sortKey?: string;
  sortValueGetter?: (record: T) => string | number | null;
};

export type Action<T> = ActionItem<T> | Divider;

type Divider = {
  type: 'divider';
};

export type ActionItem<T> = {
  label: string;
  action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown;
  condition?: (entityWithFile: T) => boolean;
  color?: string;
  disabled?: boolean;
  tooltip?: string;
};

function isDivider<T>(action: Action<T>): action is Divider {
  return 'type' in action && action.type === 'divider';
}

export default function FilesTable<T extends TableData>({
  entitiesWithFile,
  isLoading,
  primaryAction,
  additionalColumns = [],
  onClick = () => {},
  actions = [],
  enableDragDrop = false,
  inlineActions,
  nameSuffix,
  nameSubtitle,
  actionsColumnTitle,
  nameColumnTitle = 'Name',
  variant = 'default',
  readOnly = false,
  isRowReadOnly,
  treeRows,
  onToggleExpand,
  selection,
}: Props<T>) {
  const compact = variant === 'compact';
  const treeMode = treeRows !== undefined;
  const resolvedActionsColumnTitle = actionsColumnTitle ?? (compact ? '' : 'Actions');
  const effectiveDragDrop = enableDragDrop && !readOnly;

  const columns: SortableColumn<T>[] = useMemo(() => {
    const treeRowById = treeRows ? new Map(treeRows.map(r => [r.record.id, r])) : undefined;
    return [
      {
        accessor: 'file.fileName',
        title: buildColumnTitle(nameColumnTitle, !treeMode, compact),
        sortable: !treeMode,
        sortKey: 'file.fileName',
        render: record => {
          const flatRow = treeRowById?.get(record.id);
          const depth = flatRow?.depth ?? 0;
          const hasChildren = flatRow?.hasChildren ?? false;
          const isExpanded = flatRow?.isExpanded ?? false;

          const suffix = nameSuffix?.(record);

          const nameContent = (
            <FileNameWithIcon
              isFolder={Boolean(record.isFolder)}
              isGenerated={Boolean(record.isGenerated)}
              fileName={record.file.fileName}
              fileSize={record.file.fileSize}
              subtitle={buildSubtitle(record, nameSubtitle)}
              variant={variant}
              isSelected={selection?.selectedIds.has(record.id) ?? false}
            />
          );

          const nameWithSuffix = suffix ? (
            <Group gap="xs" wrap="nowrap">
              {nameContent}
              {suffix}
            </Group>
          ) : (
            nameContent
          );

          if (!treeMode) {
            return nameWithSuffix;
          }

          return (
            <Flex align="center" gap={0} pl={`calc(${depth} * var(--mantine-spacing-md))`}>
              {hasChildren ? (
                <Box
                  c="gray.6"
                  style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
                  mr="xxs"
                  data-testid={`tree-chevron-${record.id}`}
                >
                  {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                </Box>
              ) : (
                <Box w={16} mr="xxs" style={{ flexShrink: 0 }} />
              )}
              {nameWithSuffix}
            </Flex>
          );
        },
      },
      ...additionalColumns.map(col => ({
        accessor: col.label,
        title: buildColumnTitle(col.label, treeMode ? false : col.sortable, compact),
        textAlign: col.textAlign,
        sortable: treeMode ? false : col.sortable,
        sortKey: col.sortKey,
        sortValueGetter: col.sortValueGetter,
        render: (record: T) => col.valueBuilder(record),
      })),
      {
        accessor: 'actions',
        title: resolvedActionsColumnTitle,
        textAlign: 'right' as const,
        render: record => {
          const resolvedActions = typeof actions === 'function' ? actions(record) : (actions ?? []);
          return (
            <Group
              justify={compact ? 'flex-end' : 'space-between'}
              gap={compact ? 'lg' : undefined}
              wrap="nowrap"
            >
              <PrimaryActionButton primaryAction={primaryAction} entityWithFile={record} />
              {'categoryScope' in record ? null : inlineActions?.(record)}
              {selection ? (
                <Box w={20} h={20} />
              ) : (
                !readOnly &&
                resolvedActions.length > 0 && (
                  <ActionMenu entityWithFile={record} actions={resolvedActions} compact={compact} />
                )
              )}
            </Group>
          );
        },
      },
      ...(selection ? [buildSelectionColumn(selection)] : []),
    ];
  }, [
    additionalColumns,
    primaryAction,
    actions,
    inlineActions,
    nameSuffix,
    nameSubtitle,
    actionsColumnTitle,
    nameColumnTitle,
    compact,
    readOnly,
    treeMode,
    treeRows,
    variant,
    selection,
  ]);

  const { sortStatus, setSortStatus, sortedData } = useSortableData(entitiesWithFile, columns, {
    columnAccessor: 'file.createdAt',
    direction: 'desc',
  });

  const dragDropContext = useFileDragDrop();

  const rowFactory = useCallback(
    ({
      record,
      children,
      rowProps,
    }: {
      record: T;
      index: number;
      children: React.ReactNode;
      rowProps: TableTrProps;
      expandedElement?: React.ReactNode;
    }) => {
      const handleRowClick = (e: React.MouseEvent) => {
        if (dragDropContext?.draggedItem) {
          e.preventDefault();
          return;
        }
        if (treeMode && record.isFolder) {
          onToggleExpand?.(record.id);
          return;
        }
        onClick(record);
      };

      const handleDragStart = (e: React.DragEvent) => {
        if (dragDropContext) {
          e.dataTransfer.effectAllowed = 'move';
          dragDropContext.setDraggedItem(record);
          createDragElement(record, e);
        }
      };

      const handleDragEnd = () => {
        if (dragDropContext) {
          dragDropContext.setDraggedItem(null);
          dragDropContext.setIsDraggedOver(null);
        }
      };

      const rowIsReadOnly = isRowReadOnly?.(record) ?? false;

      const handleDragOver = (e: React.DragEvent) => {
        if (readOnly) return;
        // Category rows are synthetic sentinels with no real file id — they cannot be drop targets.
        if ('categoryScope' in record) return;
        // Per-row read-only rows cannot receive drops.
        if (rowIsReadOnly) return;
        if (dragDropContext && record.isFolder && dragDropContext.canDropOnTarget(record.id)) {
          e.preventDefault();
          dragDropContext.setIsDraggedOver(record.id);
        }
      };

      const handleDragLeave = () => {
        if (dragDropContext) {
          dragDropContext.setIsDraggedOver(null);
        }
      };

      const handleDrop = async (e: React.DragEvent) => {
        if (readOnly) return;
        e.preventDefault();
        // Category rows are synthetic sentinels with no real file id — they cannot be drop targets.
        if ('categoryScope' in record) return;
        // Per-row read-only rows cannot receive drops.
        if (rowIsReadOnly) return;
        if (
          !dragDropContext ||
          !record.isFolder ||
          !dragDropContext.draggedItem ||
          !dragDropContext.canDropOnTarget(record.id)
        ) {
          return;
        }
        try {
          await dragDropContext.handleMoveFile(dragDropContext.draggedItem, record.id);
        } finally {
          dragDropContext.setDraggedItem(null);
          dragDropContext.setIsDraggedOver(null);
        }
      };

      const isDragging = dragDropContext?.isDragging(record.id);
      const isDropTarget = dragDropContext?.isDropTarget(record.id);
      let draggable = false;
      if (!('categoryScope' in record) && !rowIsReadOnly) {
        draggable = effectiveDragDrop && !!dragDropContext;
      }

      let className = rowProps.className || '';
      if (compact) {
        className += ` ${classes.compactRow}`;
      }
      if (record.isFolder) {
        className += ` ${classes.folderRow}`;
      }
      if (selection?.selectedIds.has(record.id)) {
        className += ` ${classes.selectedRow}`;
      }
      if (draggable) {
        className += ` ${classes.draggableRow}`;
        if (isDragging) {
          className += ` ${classes.dragging}`;
        }
        if (isDropTarget && record.isFolder) {
          className += ` ${classes.dropTarget}`;
        }
      }

      return (
        <MantineTable.Tr
          {...rowProps}
          className={className.trim()}
          onClick={handleRowClick}
          draggable={draggable}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {children}
        </MantineTable.Tr>
      );
    },
    [
      dragDropContext,
      onClick,
      effectiveDragDrop,
      readOnly,
      compact,
      treeMode,
      onToggleExpand,
      isRowReadOnly,
      selection,
    ],
  );

  const visibleRecords = treeMode ? (treeRows.map(r => r.record) as unknown as T[]) : sortedData;

  // DataTable has a discriminated union on sortStatus/onSortStatusChange — spread conditionally.
  const sortProps = treeMode ? {} : { sortStatus, onSortStatusChange: setSortStatus };

  const dataTableProps = {
    variant: 'bare' as const,
    columns,
    records: visibleRecords,
    fetching: isLoading,
    ...sortProps,
    verticalSpacing: compact ? ('xxs' as const) : ('sm' as const),
    minHeight: isLoading || visibleRecords.length === 0 ? 200 : undefined,
    rowFactory,
    classNames: compact ? { header: classes.compactHeader } : undefined,
    sortIcons: compact ? { sorted: <></>, unsorted: <></> } : undefined,
    borderColor: compact ? 'gray.1' : undefined,
    rowBorderColor: compact ? 'gray.1' : undefined,
  };

  return (
    <Box>
      <DataTable {...dataTableProps} />
    </Box>
  );
}

function buildSelectionColumn<T extends TableData>(selection: Selection<T>): SortableColumn<T> {
  return {
    accessor: 'select',
    title: '',
    textAlign: 'right',
    render: record => <SelectionCheckbox record={record} selection={selection} />,
  };
}

function buildColumnTitle(label: string, sortable: boolean | undefined, compact: boolean) {
  if (!compact || !sortable) {
    return label;
  }
  return (
    <Group gap="xxxs" wrap="nowrap">
      <IconArrowsSort size={16} />
      <span>{label}</span>
    </Group>
  );
}

function buildSubtitle<T extends TableData>(
  record: T,
  nameSubtitle?: (record: T) => string | undefined,
) {
  if (!nameSubtitle) {
    return formatDateTime(record.createdAt);
  }
  return nameSubtitle(record) ?? '';
}

type SelectionCheckboxProps<T extends TableData> = {
  record: T;
  selection: Selection<T>;
};

const SelectionCheckbox = <T extends TableData>({
  record,
  selection,
}: SelectionCheckboxProps<T>) => {
  if (selection.isSelectable?.(record) === false) {
    return null;
  }
  return (
    <Checkbox
      color="brand"
      size="xs"
      radius={6}
      checked={selection.selectedIds.has(record.id)}
      onChange={() => selection.onToggle(record.id)}
      onClick={e => e.stopPropagation()}
    />
  );
};

type PrimaryActionButtonProps<T extends TableData> = {
  primaryAction?: PrimaryActionType<T> | ((entityWithFile: T) => PrimaryActionType<T> | undefined);
  entityWithFile: T;
};

const PrimaryActionButton = <T extends TableData>({
  primaryAction,
  entityWithFile,
}: PrimaryActionButtonProps<T>) => {
  const resolvedPrimaryAction =
    typeof primaryAction === 'function' ? primaryAction(entityWithFile) : primaryAction;

  const handleActionClick = useCallback(() => {
    if (resolvedPrimaryAction?.type === 'action') {
      resolvedPrimaryAction.onClick(entityWithFile);
    }
  }, [resolvedPrimaryAction, entityWithFile]);

  if (!resolvedPrimaryAction) {
    return <Box />;
  }

  const isDisabled =
    resolvedPrimaryAction.condition && !resolvedPrimaryAction.condition(entityWithFile);
  const variant = resolvedPrimaryAction.variant || 'primary';
  const IconComponent = resolvedPrimaryAction.icon || IconArrowRight;

  if (resolvedPrimaryAction.type === 'link') {
    return (
      <Button
        variant={variant}
        component={NavLink}
        disabled={isDisabled}
        onClick={() => {
          if (resolvedPrimaryAction.onClick) {
            resolvedPrimaryAction.onClick(entityWithFile);
          }
        }}
        to={resolvedPrimaryAction.getLink(entityWithFile)}
        rightSection={<IconComponent size={16} />}
      >
        {resolvedPrimaryAction.label}
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      disabled={isDisabled}
      onClick={e => {
        e.stopPropagation();
        handleActionClick();
      }}
      rightSection={<IconComponent size={16} />}
    >
      {resolvedPrimaryAction.label}
    </Button>
  );
};

type ActionMenuProps<T extends TableData> = {
  entityWithFile: T;
  actions?: Action<T>[] | ((entityWithFile: T) => Action<T>[]);
  compact?: boolean;
};

const ActionMenu = <T extends TableData>({
  entityWithFile,
  actions,
  compact = false,
}: ActionMenuProps<T>) => {
  const resolvedActions = typeof actions === 'function' ? actions(entityWithFile) : actions;

  const handleMenuIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleMenuItemClick = useCallback(
    (action: (entityWithFile: T, anchor?: HTMLAnchorElement) => unknown) => {
      return () => {
        const a = document.createElement('a');
        action(entityWithFile, a);
      };
    },
    [entityWithFile],
  );

  return (
    <Menu
      transitionProps={{ transition: 'pop-top-right' }}
      position="bottom-end"
      width={220}
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          data-testid="doc-three-dots"
          onClick={handleMenuIconClick}
          style={
            compact
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 8,
                }
              : undefined
          }
        >
          {compact ? (
            <IconDotsVertical color="var(--mantine-color-gray-6)" stroke={2} size={16} />
          ) : (
            <IconDots color="#475467" stroke={2} size={20} />
          )}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown data-testid="doc-actions-menu">
        {resolvedActions?.map((a, i) => {
          if (isDivider(a)) {
            return <Menu.Divider key={i} />;
          }
          if (a.condition && !a.condition(entityWithFile)) {
            return null;
          }
          if (a.disabled && a.tooltip) {
            return (
              <Tooltip key={i} label={a.tooltip}>
                <Menu.Item disabled color={a.color}>
                  {a.label}
                </Menu.Item>
              </Tooltip>
            );
          }
          return (
            <Menu.Item
              key={i}
              color={a.color}
              disabled={a.disabled}
              onClick={e => {
                e.stopPropagation();
                handleMenuItemClick(a.action)();
              }}
            >
              {a.label}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
